"use client";

import { useState } from "react";
import { cn } from "@repo/ui/utils";
import { ColorBlock } from "@repo/ui/color-block";
import { IconButton } from "@repo/ui/icon-button";
import { SolidShape } from "@repo/ui/solid-shape";
import { VisualWrapper } from "@/components/common/visual/visual.component";
import { RichTextCondensed } from "@/components/common/rich-text/rich-text-condensed.component";
import type { FeaturedListProps } from "./featured-list.types";

/**
 * FeaturedList presentational component.
 *
 * Renders a history timeline/stepper with a list of featured items on an ocean
 * blue ColorBlock background.
 *
 * Desktop: sidebar navigation (all item labels), image, and heading + body.
 * Mobile: single-item view with pagination arrows.
 */
export function FeaturedList({ items, className }: FeaturedListProps) {
  const [activeIndex, setActiveIndex] = useState(0);

  if (!items?.length) {
    return null;
  }

  const goToPrevious = () => {
    setActiveIndex((prev) => (prev === 0 ? items.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setActiveIndex((prev) => (prev === items.length - 1 ? 0 : prev + 1));
  };

  return (
    <ColorBlock
      variant="rectangle-sm"
      color="ocean"
      className={cn(
        "text-white [&>svg]:hidden desktop:[&>svg]:block",
        className,
      )}
      data-testid="featured-list"
    >
      {/* Desktop layout */}
      <div className="hidden desktop:block">
        <div className="container-grid py-[120px]">
          <div className="col-span-full grid grid-cols-subgrid">
            {/* Nav sidebar — active item shows an inline diamond marker
             * before the text; inactive items render text only, flush
             * to the left margin. */}
            <nav className="col-span-2 flex flex-col gap-6">
              {items.map((item, index) => {
                const isActive = activeIndex === index;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    aria-label={item.label ?? `Item ${index + 1}`}
                    aria-pressed={isActive}
                    className={cn(
                      "flex items-center gap-2 py-4 text-left typography-component-md-medium cursor-pointer",
                      isActive ? "text-white" : "text-deep-sea-blue-300",
                    )}
                  >
                    {isActive && (
                      <SolidShape
                        variant="concave-square"
                        color="white"
                        animated={false}
                        className="h-[6px] w-[6px] shrink-0"
                      />
                    )}
                    <span>{item.label ?? `Item ${index + 1}`}</span>
                  </button>
                );
              })}
            </nav>

            {/* Image — all items stacked, fade between */}
            <div className="col-span-4 col-start-4 grid">
              {items.map((item, index) => (
                <div
                  key={item.key}
                  className={cn(
                    "col-start-1 row-start-1 transition-opacity duration-700",
                    index === activeIndex
                      ? "opacity-100"
                      : "opacity-0 pointer-events-none",
                  )}
                >
                  {item.visual && (
                    <div className="relative w-full aspect-[421/544] overflow-hidden rounded-md">
                      <VisualWrapper
                        visual={item.visual}
                        asBackground
                        className="w-full h-full"
                        visualClassName="w-full h-full"
                        imgClassName="object-cover"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Text content — all items stacked, fade between */}
            <div className="col-span-4 col-start-9 grid">
              {items.map((item, index) => (
                <div
                  key={item.key}
                  className={cn(
                    "col-start-1 row-start-1 flex flex-col justify-between transition-opacity duration-700",
                    index === activeIndex
                      ? "opacity-100"
                      : "opacity-0 pointer-events-none",
                  )}
                >
                  {item.headline && (
                    <h3 className="typography-desktop-heading-h3-voyage">
                      {item.headline}
                    </h3>
                  )}
                  {item.body && (
                    <RichTextCondensed
                      richText={item.body}
                      as="body-md-medium-flat"
                      compact
                      className="text-white"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile layout */}
      <div className="desktop:hidden">
        <div className="container-grid py-16">
          <div className="col-span-full flex flex-col gap-9">
            {/* All items stacked, fade between — active in flow, inactive absolute */}
            <div className="relative">
              {items.map((item, index) => (
                <div
                  key={item.key}
                  className={cn(
                    "flex flex-col gap-9 transition-opacity duration-700",
                    index === activeIndex
                      ? "relative opacity-100"
                      : "absolute inset-x-0 top-0 opacity-0 pointer-events-none",
                  )}
                >
                  {/* Label */}
                  {item.label && (
                    <div className="flex items-center gap-2 py-4">
                      <SolidShape
                        variant="concave-square"
                        color="white"
                        animated={false}
                        className="h-[6px] w-[6px] shrink-0"
                      />
                      <span className="typography-component-md-medium">
                        {item.label}
                      </span>
                    </div>
                  )}

                  {/* Image */}
                  {item.visual && (
                    <div className="relative w-full aspect-[358/312] overflow-hidden rounded-md">
                      <VisualWrapper
                        visual={item.visual}
                        asBackground
                        className="w-full h-full"
                        visualClassName="w-full h-full"
                        imgClassName="object-cover"
                      />
                    </div>
                  )}

                  {/* Heading + body */}
                  <div className="flex flex-col gap-9">
                    {item.headline && (
                      <h3 className="typography-mobile-heading-h3-voyage">
                        {item.headline}
                      </h3>
                    )}
                    {item.body && (
                      <RichTextCondensed
                        richText={item.body}
                        as="body-sm-medium-flat"
                        compact
                        className="text-white"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination arrows */}
            {items.length > 1 && (
              <div className="flex items-center gap-2">
                <IconButton
                  variant="white"
                  aria-label="Previous item"
                  icon="arrow-left"
                  onClick={goToPrevious}
                />
                <IconButton
                  variant="white"
                  aria-label="Next item"
                  icon="arrow-right"
                  onClick={goToNext}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </ColorBlock>
  );
}
